import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import Recaptcha from '../../components/common/Recaptcha';
import PublicNav from '../../components/layout/PublicNav';
import PublicFooter from '../../components/layout/PublicFooter';
import logo from '../../assets/logo.png';

const Logo = () => (
  <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.75rem' }}>
      <img src={logo} alt="PCredit" style={{ height: 40, width: 'auto' }} />
    </div>
  </div>
);

// Step 1: Enter email
function RequestStep({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await authService.requestPasswordReset(email, captchaToken);
      onSuccess(email);
    } catch {
      setError('Failed to send reset code. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.4rem' }}>
          Forgot your password?
        </h2>
        <p style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
          Enter your email and we'll send a reset code.
        </p>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span><span>{error}</span></div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">Email address</label>
          <input
            className="form-input"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            placeholder="you@example.com"
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0' }}>
          <Recaptcha onChange={setCaptchaToken} />
        </div>
        <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading || !captchaToken}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin .6s linear infinite', display: 'inline-block' }} />
              Sending…
            </span>
          ) : 'Send reset code'}
        </button>
      </form>
    </>
  );
}

// Step 2: OTP + new password
function ConfirmStep({ email, onDone }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const inputRefs = useRef([]);

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowLeft' && i > 0) inputRefs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otp];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      await authService.confirmPasswordReset({ email, otp: otp.join(''), new_password: password, confirm_new_password: confirm, recaptcha_token: captchaToken });
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code.');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.4rem' }}>
          Enter reset code
        </h2>
        <p style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
          We sent a 6-digit code to <strong style={{ color: 'var(--text-1)' }}>{email}</strong>
        </p>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span><span>{error}</span></div>}

      <form onSubmit={handleSubmit}>
        {/* OTP boxes */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.75rem' }}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              className="otp-input"
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleOtpChange(i, e.target.value)}
              onKeyDown={e => handleOtpKeyDown(i, e)}
              onPaste={i === 0 ? handleOtpPaste : undefined}
              autoFocus={i === 0}
            />
          ))}
        </div>

        <div className="form-group">
          <label className="form-label">New password</label>
          <div style={{ position: 'relative' }}>
            <input
              className="form-input"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required minLength={8}
              placeholder="Min 8 characters"
              style={{ paddingRight: '2.75rem' }}
            />
            <button type="button" onClick={() => setShowPass(!showPass)}
              style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', fontSize: '1rem' }}>
              {showPass ? '🙈' : '👁'}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Confirm new password</label>
          <input
            className="form-input"
            type={showPass ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required
            placeholder="Repeat password"
          />
          {confirm && password !== confirm && <span className="form-error">Passwords don't match</span>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0' }}>
          <Recaptcha onChange={setCaptchaToken} />
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg"
          style={{ width: '100%' }}
          disabled={loading || otp.some(d => !d) || !password || !confirm || !captchaToken}
        >
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin .6s linear infinite', display: 'inline-block' }} />
              Resetting…
            </span>
          ) : 'Reset password'}
        </button>
      </form>
    </>
  );
}

// Step 3: Success
function SuccessStep({ navigate }) {
  return (
    <div style={{ textAlign: 'center', padding: '1rem 0' }}>
      <div style={{
        width: 72, height: 72, borderRadius: '50%',
        background: 'rgba(0,200,150,0.12)', border: '1px solid rgba(0,200,150,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 1.25rem', fontSize: '2rem',
      }}>✓</div>
      <h2 style={{ fontSize: '1.375rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.5rem' }}>
        Password reset!
      </h2>
      <p style={{ color: 'var(--text-2)', marginBottom: '2rem', fontSize: '0.9rem' }}>
        Your password has been updated successfully.
      </p>
      <button
        className="btn btn-primary btn-lg"
        style={{ width: '100%' }}
        onClick={() => navigate('/login')}
      >
        Sign in now
      </button>
    </div>
  );
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PublicNav />
      <div className="auth-bg" style={{ flex: 1, minHeight: 0 }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        <Logo />

        <div className="auth-card">
          {/* Step indicator */}
          {step < 3 && (
            <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '2rem' }}>
              {[1, 2].map(s => (
                <div key={s} style={{
                  flex: 1, height: 3, borderRadius: 99,
                  background: s <= step ? 'var(--primary)' : 'var(--border-bright)',
                  transition: 'background .3s',
                  boxShadow: s <= step ? '0 0 8px var(--primary-glow)' : 'none',
                }} />
              ))}
            </div>
          )}

          {step === 1 && (
            <RequestStep onSuccess={(e) => { setEmail(e); setStep(2); }} />
          )}
          {step === 2 && (
            <ConfirmStep email={email} onDone={() => setStep(3)} />
          )}
          {step === 3 && <SuccessStep navigate={navigate} />}

          {step < 3 && (
            <>
              <div className="glow-divider" style={{ margin: '1.5rem 0' }} />
              <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-2)' }}>
                <Link to="/login" style={{ fontWeight: 600 }}>← Back to sign in</Link>
              </p>
            </>
          )}
        </div>
      </div>
      </div>
      <PublicFooter />
    </div>
  );
}
